import React, { useEffect, useMemo, useRef, useState } from 'react';
import RadarChart from '../shared/RadarChart';
import PhoneFrame from './PhoneFrame';
import Button from '../shared/Button';
import Icon from '../shared/Icon';
import { MOCK_FEED, MOCK_RADAR_SCORES, MOCK_WORKOUTS } from '../../utils/mockData';
import homeShot from '../../assets/img/screenshot-home.png';
import exerciseShot from '../../assets/img/screenshot-exercise-record.png';
import scheduleShot from '../../assets/img/screenshot-schedule-preview.png';
import changePopupShot from '../../assets/img/screenshot-fitndex-change-popup.png';
import './PromoSections.css';

const STORE_URL = 'https://fitndex.com/app/add';
const KAELIS_PRICE = 59.99;

const MARQUEE_ITEMS = [
  'Video Analysis', 'GPS Tracking', 'Radar Intelligence', 'Work Output', 'Scheduling', 'Feed',
];

const FEATURE_GRID = [
  { key: 'media', icon: 'camera', title: 'Media Upload', body: 'Lift videos and machine photos, logged in seconds.', detail: 'Attach video or photo evidence to any set — review your own form anytime, frame by frame.' },
  { key: 'gps', icon: 'map-pin', title: 'GPS Record', body: 'Track runs, rides, and hikes with live route capture.', detail: 'Pace, distance, and route auto-feed into your work output — no manual entry required.' },
  { key: 'journal', icon: 'edit', title: 'Journal Entry', body: 'Words, sets, reps, and distance — your way.', detail: 'Free-form notes attach to any log entry, searchable later from your training history.' },
  { key: 'scan', icon: 'assign', title: 'Body Scan', body: 'Measurements synced straight into your profile.', detail: 'Track weight, body-fat, and girth measurements over time alongside your performance trend.' },
];

const FEATURE_ROWS = [
  {
    key: 'log',
    kicker: 'Log Everything',
    title: 'Every session, instantly logged.',
    body: 'Upload a lift video, record a run with GPS, write a journal entry, or scan your measurements — one home screen for every way you train.',
    bullets: ['Video and machine-photo uploads', 'GPS-tracked runs, rides, and hikes', 'Journal entries and body measurements'],
    cta: 'See it in the app',
    shot: { src: exerciseShot, alt: 'Exercise record detail with embedded video', tilt: -3 },
  },
  {
    key: 'output',
    kicker: 'Universal Output',
    title: 'One unit. Every exercise. Every athlete.',
    body: 'A push-up, a deadlift, a 5k run — every set converts to the same physics-backed unit of work. No more guessing who trained harder: compare your output to anyone\u2019s, on any exercise, apples to apples.',
    bullets: ['Total Work (Joules) per rep and per set', 'Average Power (Watts) for every session', 'Same units across every exercise and every athlete'],
    cta: 'See the math behind it',
    shot: { src: exerciseShot, alt: 'Exercise record work breakdown with total work and average power', tilt: -2 },
  },
  {
    key: 'schedule',
    kicker: 'Your Schedule',
    title: 'Plan the week, see the day.',
    body: 'Program flow and daily intent live in one calendar — plan your own week, then see exactly what today asks of you.',
    bullets: ['Weekly and monthly load planning', 'Day-level workout previews', 'Fast drill-down into upcoming sessions'],
    cta: 'Preview the calendar',
    shot: { src: scheduleShot, alt: 'Weekly schedule preview with locked workout details', tilt: 3 },
  },
  {
    key: 'changes',
    kicker: 'Fully Transparent',
    title: 'See exactly what changed.',
    body: 'Every logged set shows its effect on your rating — which movement drivers moved, and by how much, the moment you save.',
    bullets: ['Per-driver before/after breakdown', 'No black-box scoring', 'Understand your number, every time'],
    cta: 'See a real breakdown',
    shot: { src: changePopupShot, alt: 'What changed breakdown after a logged set', tilt: -2 },
  },
];

const FAQ_ITEMS = [
  {
    q: 'How is the FITNDEX Rating calculated?',
    a: 'Every logged set maps to one or more of 18 primary movement drivers across three capacities — strength, work capacity, and endurance. The rating is a live, objective composite of all of it, not a single lift or session.',
  },
  {
    q: 'Is my training data private?',
    a: 'Yes. Your logs, videos, and measurements are visible only to you. Nothing is shared publicly without your action.',
  },
  {
    q: 'Does it work offline?',
    a: 'Logging works offline as a PWA; entries sync the next time you have a connection.',
  },
];

const PRICING_TIERS = [
  {
    key: 'free',
    name: 'Free',
    badge: 'Your Plan',
    note: 'For everyday training',
    price: 0,
    priceLabel: '$0',
    items: [
      ['Body calibrations', '1 / mo'],
      ['New drafts', '10 / mo'],
      ['Scheduling', 'Unlimited'],
      ['Manual logs', '100 / mo'],
    ],
  },
  {
    key: 'gold',
    name: 'Gold',
    badge: '14 Day Free Trial',
    note: 'Everyday training',
    price: 6.99,
    priceLabel: '$6.99',
    billedNote: '$5.83/month billed annually',
    items: [
      ['Body calibrations', '4 / mo'],
      ['New drafts', 'Unlimited'],
      ['Scheduling', 'Unlimited'],
      ['Program imports', '10 / mo'],
      ['Smart edits', '90 / mo'],
      ['Video analyses', '30 / mo'],
      ['GPS map loads', '30 / mo'],
      ['Photo logs', '60 / mo'],
      ['Manual logs', '300 / mo'],
    ],
    storage: '25 GB',
  },
  {
    key: 'platinum',
    name: 'Platinum',
    badge: 'Most Popular',
    featured: true,
    note: 'Serious volume',
    price: 14.99,
    priceLabel: '$14.99',
    billedNote: '$12.50/month billed annually',
    items: [
      ['Body calibrations', '12 / mo'],
      ['New drafts', 'Unlimited'],
      ['Scheduling', 'Unlimited'],
      ['Program imports', '30 / mo'],
      ['Smart edits', '225 / mo'],
      ['Video analyses', '75 / mo'],
      ['GPS map loads', '75 / mo'],
      ['Photo logs', '150 / mo'],
      ['Manual logs', '750 / mo'],
    ],
    storage: '100 GB',
  },
  {
    key: 'diamond',
    name: 'Diamond',
    note: 'Maximum headroom',
    price: 29.99,
    priceLabel: '$29.99',
    billedNote: '$22.92/month billed annually',
    items: [
      ['Body calibrations', '30 / mo'],
      ['New drafts', 'Unlimited'],
      ['Scheduling', 'Unlimited'],
      ['Program imports', '100 / mo'],
      ['Smart edits', '450 / mo'],
      ['Video analyses', '150 / mo'],
      ['GPS map loads', '150 / mo'],
      ['Photo logs', '300 / mo'],
      ['Manual logs', '1,500 / mo'],
    ],
    storage: '250 GB',
  },
];

const TRUST_BADGES = ['Mobile-First', 'Objective Scoring', 'Built for Athletes'];

// Fades each direct child in as it scrolls into view, one time only.
function useScrollReveal() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll('.reveal-on-scroll');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return rootRef;
}

// Animates a number from 0 to target once its node scrolls into view.
function useCountUp(target, duration = 1200) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const start = performance.now();

          function tick(now) {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - (1 - t) ** 3;
            setValue(target * eased);
            if (t < 1) requestAnimationFrame(tick);
          }

          requestAnimationFrame(tick);
          observer.unobserve(el);
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return [ref, value];
}

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`promo-faq__item${open ? ' promo-faq__item--open' : ''}`}>
      <button type="button" className="promo-faq__question" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {item.q}
        <Icon name="forward" size={16} className="promo-faq__chevron" />
      </button>
      <div className="promo-faq__answer-shell">
        <p className="promo-faq__answer">{item.a}</p>
      </div>
    </div>
  );
}

export default function PromoSections({ onJoinWaitlist = () => {}, onBetaAccess = () => {} }) {
  const sectionRef = useScrollReveal();
  const teamScores = MOCK_RADAR_SCORES.team;
  const scoreValues = Object.values(teamScores);
  const fitndexRating = Math.round((scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length) * 1000) / 10;

  const feedEngagement = MOCK_FEED.reduce((acc, item) => acc + (item.likes || 0), 0);
  const upcomingSessions = MOCK_WORKOUTS.filter((w) => w.scheduledDate >= new Date().toISOString().slice(0, 10)).length;

  const [feedRef, feedValue] = useCountUp(feedEngagement);
  const [sessionsRef, sessionsValue] = useCountUp(upcomingSessions);

  const [selectedTier, setSelectedTier] = useState('platinum');
  const [kaelisEnabled, setKaelisEnabled] = useState(false);
  const [activeFeatureCard, setActiveFeatureCard] = useState(null);

  const activeTier = useMemo(() => PRICING_TIERS.find((t) => t.key === selectedTier), [selectedTier]);
  const kaelisActive = kaelisEnabled && selectedTier === 'platinum';
  const total = (activeTier?.price || 0) + (kaelisActive ? KAELIS_PRICE : 0);

  return (
    <section className="promo-sections" id="promo-sections" ref={sectionRef}>
      <div className="promo-sections__intro reveal-on-scroll">
        <p className="promo-sections__label">The App, In Action</p>
        <h2 className="promo-sections__heading">One score. Every athlete.</h2>
        <p className="promo-sections__sub">
          Real screens from the live build — profile context, training output, and scheduling intelligence, unified in one platform.
        </p>
      </div>

      <div className="promo-marquee reveal-on-scroll" aria-hidden="true">
        <div className="promo-marquee__track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span className="promo-marquee__item" key={`${item}-${i}`}>{item}</span>
          ))}
        </div>
      </div>

      <div className="promo-feature-grid reveal-on-scroll">
        <div className="promo-feature-grid__cards">
          {FEATURE_GRID.map((f, i) => (
            <div
              key={f.key}
              className="promo-feature-card"
              style={{ transitionDelay: `${i * 80}ms` }}
              tabIndex={0}
              onMouseEnter={() => setActiveFeatureCard(f.key)}
              onMouseLeave={() => setActiveFeatureCard(null)}
              onFocus={() => setActiveFeatureCard(f.key)}
              onBlur={() => setActiveFeatureCard(null)}
            >
              <span className="promo-feature-card__icon"><Icon name={f.icon} size={20} /></span>
              <h4 className="promo-feature-card__title">{f.title}</h4>
              <p className="promo-feature-card__body">{f.body}</p>
              {activeFeatureCard === f.key && (
                <div className="promo-feature-card__toast" role="tooltip">{f.detail}</div>
              )}
            </div>
          ))}
        </div>
        <div className="promo-feature-grid__visual">
          <PhoneFrame src={homeShot} alt="FITNDEX home screen with quick-log actions" tilt={2} size="md" />
        </div>
      </div>

      <div className="promo-radar-spotlight reveal-on-scroll">
        <div className="promo-radar-spotlight__content">
          <p className="promo-radar-spotlight__kicker">Radar Intelligence</p>
          <h3 className="promo-radar-spotlight__title">12 axes. One rating.</h3>
          <p className="promo-radar-spotlight__body">
            Every set and session maps across the full movement spectrum.
            The result is one FITNDEX rating — objective, comparable, and always live.
          </p>
          <div className="promo-radar-spotlight__metrics">
            <div className="promo-radar-metric">
              <span className="promo-radar-metric__label">FITNDEX Rating</span>
              <span className="promo-radar-metric__value">{fitndexRating}</span>
            </div>
            <div className="promo-radar-metric">
              <span className="promo-radar-metric__label">Profiles Mapped</span>
              <span className="promo-radar-metric__value">12 Axes</span>
            </div>
            <div className="promo-radar-metric">
              <span className="promo-radar-metric__label">Signal Confidence</span>
              <span className="promo-radar-metric__value">High</span>
            </div>
          </div>
        </div>

        <div className="promo-radar-spotlight__chart-shell">
          <RadarChart scores={teamScores} size={280} className="promo-radar-spotlight__chart" />
        </div>
      </div>

      <div className="promo-sections__proof-strip reveal-on-scroll">
        <div className="promo-proof">
          <div className="promo-proof__value">Joules &amp; Watts</div>
          <div className="promo-proof__label">The same physics-based units, every set</div>
        </div>
        <div className="promo-proof">
          <div className="promo-proof__value" ref={feedRef}>{Math.round(feedValue)}</div>
          <div className="promo-proof__label">Feed interactions this week</div>
        </div>
        <div className="promo-proof">
          <div className="promo-proof__value" ref={sessionsRef}>{Math.round(sessionsValue)}</div>
          <div className="promo-proof__label">Sessions queued this week</div>
        </div>
      </div>

      {FEATURE_ROWS.map((row, i) => (
        <div key={row.key} className={`promo-feature-row promo-feature-row--${i % 2 === 0 ? 'a' : 'b'} reveal-on-scroll`}>
          <div className="promo-feature-row__content">
            <p className="promo-feature-row__kicker">{row.kicker}</p>
            <h3 className="promo-feature-row__title">{row.title}</h3>
            <p className="promo-feature-row__body">{row.body}</p>
            <ul className="promo-feature-row__list">
              {row.bullets.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <span className="promo-feature-row__cta anim-shimmer-hover">{row.cta} &rarr;</span>
          </div>

          <div className="promo-feature-row__visual">
            <PhoneFrame src={row.shot.src} alt={row.shot.alt} tilt={row.shot.tilt} size="md" className="promo-feature-row__phone" />
          </div>
        </div>
      ))}

      <div className="promo-faq reveal-on-scroll">
        <p className="promo-faq__kicker">Questions</p>
        <h3 className="promo-faq__title">Everything you're wondering.</h3>
        <div className="promo-faq__list">
          {FAQ_ITEMS.map((item) => (
            <FaqItem item={item} key={item.q} />
          ))}
        </div>
      </div>

      <div className="promo-store reveal-on-scroll">
        <p className="promo-store__eyebrow">how much you train.</p>
        <span className="promo-store__pill">
          <Icon name="forward" size={13} />
          See how it works
        </span>

        <div className="promo-store__tiers">
          {PRICING_TIERS.map((tier) => {
            const isSelected = selectedTier === tier.key;
            return (
              <div
                key={tier.key}
                className={`promo-store__tier${tier.featured ? ' promo-store__tier--featured' : ''}${isSelected ? ' promo-store__tier--selected' : ''}`}
              >
                <button
                  type="button"
                  className="promo-store__tier-select"
                  onClick={() => setSelectedTier(tier.key)}
                  aria-pressed={isSelected}
                  aria-label={`Select ${tier.name} plan`}
                >
                  {tier.badge && <span className="promo-store__badge">{tier.badge}</span>}
                  <span className="promo-store__radio" aria-hidden="true" />
                </button>

                <h4 className="promo-store__tier-name">{tier.name}</h4>
                <div className="promo-store__tier-price">
                  {tier.priceLabel}
                  {tier.price > 0 && <span className="promo-store__tier-unit">/month</span>}
                </div>
                {tier.billedNote && <p className="promo-store__tier-billed">{tier.billedNote}</p>}
                <p className="promo-store__tier-note">{tier.note}</p>

                <ul className="promo-store__tier-items">
                  {tier.items.map(([label, value]) => (
                    <li key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </li>
                  ))}
                </ul>

                {tier.storage && (
                  <div className="promo-store__storage">
                    <span>Storage</span>
                    <strong>{tier.storage}</strong>
                  </div>
                )}

                {tier.key === 'platinum' && (
                  <div className="promo-store__kaelis">
                    <p className="promo-store__kaelis-title">Let Kaelis write your training.</p>
                    <div className="promo-store__kaelis-row">
                      <span className="promo-store__kaelis-price">+${KAELIS_PRICE}/mo</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={kaelisEnabled}
                        aria-label="Toggle Let Kaelis write your training add-on"
                        className={`promo-store__toggle${kaelisEnabled ? ' promo-store__toggle--on' : ''}`}
                        onClick={() => setKaelisEnabled((v) => !v)}
                      >
                        <span className="promo-store__toggle-thumb" />
                      </button>
                    </div>
                    <p className="promo-store__kaelis-helper">Let Kaelis write your training. Cancel anytime.</p>
                    <a className="promo-store__kaelis-link" href={STORE_URL} target="_blank" rel="noopener noreferrer">Learn more</a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="promo-store__summary">
          <div className="promo-store__summary-row">
            <span>Compare all plans side by side</span>
            <span>{activeTier?.name} {activeTier?.priceLabel}/mo{kaelisActive ? ` + Kaelis $${KAELIS_PRICE}` : ''}</span>
          </div>
          <div className="promo-store__summary-row promo-store__summary-row--total">
            <span>Total</span>
            <span>${total.toFixed(2)}/month</span>
          </div>
        </div>

        <Button variant="primary" size="lg" className="anim-shimmer promo-store__cta" onClick={onJoinWaitlist}>
          Continue with {activeTier?.name}
        </Button>
        <p className="promo-store__cancel">Cancel anytime</p>
        <p className="promo-store__disclaimer">
          Prices in USD. Monthly limits reset each billing cycle.{' '}
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer">Read our full billing policy</a>
        </p>
      </div>

      <div className="promo-final-cta reveal-on-scroll">
        <h3 className="promo-final-cta__title">Build your training intelligence.</h3>
        <p className="promo-final-cta__body">Join the beta and put every rep, run, and session behind one objective score.</p>
        <div className="promo-final-cta__actions">
          <Button variant="ghost" size="lg" onClick={onJoinWaitlist}>Join Waitlist</Button>
          <Button variant="primary" size="lg" className="anim-shimmer" onClick={onBetaAccess}>Beta Tester</Button>
        </div>
        <div className="promo-final-cta__badges">
          {TRUST_BADGES.map((badge) => (
            <span key={badge} className="promo-final-cta__badge">
              <Icon name="check" size={14} />
              {badge}
            </span>
          ))}
        </div>
      </div>

      <footer className="promo-sections__footer">
        <span>United States · Copyright © 2026 FITNDEX LLC. All rights reserved.</span>
        <span className="promo-sections__footer-links">
          <a href="/privacy">Privacy Policy</a>
          <span aria-hidden="true">|</span>
          <a href="/terms">Terms of Use</a>
        </span>
      </footer>
    </section>
  );
}



